/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { forwardRef, type JSX } from "react";
import { useFocus, useHover } from "react-aria";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  constantSemanticColors,
  spacing,
} from "../../../../../global/emotion";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import type { IconButtonProps } from "../IconButton/IconButton.emotion";
import { IconButton } from "../IconButton/IconButton.emotion";
import { Text } from "../Text/Text.emotion";

const baseStyle: Interpolation = {
  WebkitTapHighlightColor: "transparent",
  alignItems: "center",
  background: colors.transparent,
  border: "none",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  padding: spacing.none,
  transition: "all 100ms",
  width: spacing.xxxl,
};

const disabledStyle: Interpolation = {
  cursor: "not-allowed",
  opacity: 0.5,
};

const stylesVariant: Record<
  NonNullable<QuickActionButtonProps["variant"]>,
  Interpolation<ThemeType>
> = {
  neutral: (theme) => ({
    color: theme.semanticColors.neutralsVeryHigh,
    ":focus:not(:disabled)": {
      color: theme.semanticColors.neutralsMedium,
    },
    ":hover:not(:disabled)": {
      color: theme.semanticColors.neutralsVeryHigh,
    },
  }),
  transparentPrimary: (theme) => ({
    color: theme.semanticColors.neutralsVeryHigh,
    ":focus:not(:disabled)": {
      color: theme.semanticColors.neutralsMedium,
    },
    ":hover:not(:disabled)": {
      color: theme.semanticColors.neutralsVeryHigh,
    },
  }),
  transparentWhite: {
    color: constantSemanticColors.onColorVeryHigh,
    ":focus:not(:disabled)": {
      color: constantSemanticColors.onColorVeryHigh,
    },
    ":hover:not(:disabled)": {
      color: constantSemanticColors.onColorHighest,
    },
  },
};

const iconStylesVariant: Record<
  NonNullable<QuickActionButtonProps["variant"]>,
  Interpolation<ThemeType>
> = {
  neutral: (theme) => ({
    ":focus:not(:disabled)": {
      backgroundColor: theme.semanticColors.neutralsVeryLow,
      color: theme.semanticColors.neutralsMedium,
    },
    ":hover:not(:disabled)": {
      backgroundColor: theme.semanticColors.neutralsVeryLow,
      color: theme.semanticColors.neutralsVeryHigh,
    },
  }),
  transparentPrimary: (theme) => ({
    ":focus:not(:disabled)": {
      backgroundColor: theme.semanticColors.neutralsLowest,
      color: theme.semanticColors.neutralsMedLow,
    },
    ":hover:not(:disabled)": {
      backgroundColor: theme.semanticColors.neutralsLowest,
      color: theme.semanticColors.neutralsVeryHigh,
    },
  }),
  transparentWhite: {
    ":focus:not(:disabled)": {
      backgroundColor: constantSemanticColors.fixedLightenMedLow,
      color: constantSemanticColors.onColorVeryHigh,
    },
    ":hover:not(:disabled)": {
      backgroundColor: constantSemanticColors.fixedLightenMedLow,
      color: constantSemanticColors.onColorHighest,
    },
  },
};

export type QuickActionButtonProps = IconButtonProps & {
  variant?: "neutral" | "transparentPrimary" | "transparentWhite";
  size?: "m" | "l";
};

export const QuickAction = forwardRef<any, QuickActionButtonProps>(
  (
    {
      children,
      disabled = false,
      asChild = false,
      variant = "transparentPrimary",
      size = "m",
      icon,
      color = colors.purple60,
      ...props
    },
    ref,
  ) => {
    const { focusProps } = useFocus({});
    const { hoverProps } = useHover({});

    const DeterminedChildren = determineChildrenInject({
      beforeChildrenInject: [
        <IconButton
          key={"iconButton"}
          asChild
          icon={icon}
          variant={variant}
          size={size}
          css={iconStylesVariant[variant]}
          disabled={disabled}
        >
          <span />
        </IconButton>,
      ],
      children: children as JSX.Element,
    });

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement: "button",
    });

    return (
      <Text asChild size={size === "l" ? "m" : "xs"} type="title">
        <DeterminedElement
          disabled={disabled}
          ref={ref}
          {...focusProps}
          {...hoverProps}
          css={[baseStyle, disabled && disabledStyle, stylesVariant[variant]]}
          {...props}
        >
          {DeterminedChildren}
        </DeterminedElement>
      </Text>
    );
  },
);

QuickAction.displayName = "QuickAction";
